import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCustomerDetails, addLead } from '../../api';
import DashboardNav from '../../components/dashboard/DashboardNav';

function AddWpLeadToCustomer({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('customerId');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customer, setCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(true);

  // WP-Daten State
  const [propertyData, setPropertyData] = useState({
    isOwner: null,
    buildingType: '',
    livingAreaSize: '',
    peopleInHouse: '',
    buildYear: '',
    hasGasOrOil: null,
    heatingOlderThan2Years: null,
    currentHeatingSystem: '',
    hasSolarThermal: null,
    solarThermalRemoved: null,
    hasMonumentProtection: null,
  });

  const [progression, setProgression] = useState(1);

  // Kundendaten laden, wenn customerId vorhanden
  useEffect(() => {
    if (customerId && user?.id) {
      const fetchCustomer = async () => {
        try {
          const data = await getCustomerDetails(user.id, customerId);
          if (data.success) {
            setCustomer(data.customer);
          } else {
            setError('Kunde nicht gefunden');
          }
        } catch (err) {
          setError('Fehler beim Laden der Kundendaten: ' + err.message);
        } finally {
          setCustomerLoading(false);
        }
      };
      fetchCustomer();
    } else {
      setCustomerLoading(false);
    }
  }, [customerId, user]);

  // Handler-Funktionen
  const handleOwnershipChange = (isOwner) => {
    setPropertyData({
      ...propertyData,
      isOwner: isOwner
    });
    if (progression === 1) {
      setProgression(2);
    }
  };

  const handleBuildingTypeChange = (type) => {
    setPropertyData({
      ...propertyData,
      buildingType: type
    });
    if (progression === 2) {
      setProgression(3);
    }
  };

  const handleLivingAreaSizeChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      livingAreaSize: value,
    });
    if (progression === 3 && value.trim() !== '') {
      setProgression(4);
    }
  };

  const handlePeopleInHouseChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      peopleInHouse: value
    });
    if (progression === 4 && value.trim() !== '') {
      setProgression(5);
    }
  };

  const handleBuildYearChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      buildYear: value
    });
    if (progression === 5 && value.trim() !== '') {
      setProgression(6);
    }
  };

  const handleGasOrOilChange = (hasGasOrOil) => {
    setPropertyData({
      ...propertyData,
      hasGasOrOil: hasGasOrOil
    });
    if (progression === 6) {
      setProgression(7);
    }
  };

  const handleHeatingOlderThan2YearsChange = (isOlderThan2Years) => {
    setPropertyData({
      ...propertyData,
      heatingOlderThan2Years: isOlderThan2Years
    });
    if (progression === 7) {
      setProgression(8);
    }
  };

  const handleCurrentHeatingSystemChange = (system) => {
    setPropertyData({
      ...propertyData,
      currentHeatingSystem: system
    });
    if (progression === 8) {
      setProgression(9);
    }
  };

  const handleSolarThermalChange = (hasSolarThermal) => {
    setPropertyData({
      ...propertyData,
      hasSolarThermal: hasSolarThermal,
      solarThermalRemoved: hasSolarThermal ? propertyData.solarThermalRemoved : null
    });
    if (progression >= 9) {
      if (hasSolarThermal) {
        setProgression(10);
      } else {
        setProgression(11);
      }
    }
  };

  const handleSolarThermalRemovedChange = (solarThermalRemoved) => {
    setPropertyData({
      ...propertyData,
      solarThermalRemoved: solarThermalRemoved
    });
    if (progression === 10) {
      setProgression(11);
    }
  };

  const handleMonumentProtectionChange = (hasProtection) => {
    setPropertyData({
      ...propertyData,
      hasMonumentProtection: hasProtection
    });
    if (progression === 11) {
      setProgression(12);
    }
  };

  // Prüfen, ob alle Pflichtfelder ausgefüllt sind
  const isStep1Valid = () => {
    return (
      propertyData.isOwner === true &&
      propertyData.buildingType !== '' &&
      propertyData.livingAreaSize.trim() !== '' &&
      propertyData.peopleInHouse.trim() !== '' &&
      propertyData.buildYear.trim() !== '' &&
      propertyData.hasGasOrOil !== null &&
      propertyData.heatingOlderThan2Years !== null &&
      propertyData.currentHeatingSystem !== '' &&
      propertyData.hasSolarThermal !== null &&
      (propertyData.hasSolarThermal === false || 
       (propertyData.hasSolarThermal === true && propertyData.solarThermalRemoved !== null)) &&
      propertyData.hasMonumentProtection !== null
    );
  };

  const isLivingAreaUnsupported = () => {
    return Number(propertyData.livingAreaSize) > 250;
  };

  // Prüfen, ob Lead qualifiziert ist
  const isLeadQualified = () => {
    return (
      propertyData.isOwner === true &&
      !isLivingAreaUnsupported() &&
      propertyData.hasGasOrOil === true &&
      propertyData.heatingOlderThan2Years === true &&
      propertyData.hasMonumentProtection === false &&
      (propertyData.hasSolarThermal === false || 
       (propertyData.hasSolarThermal === true && propertyData.solarThermalRemoved === true))
    );
  };

  // Prüfen, ob Lead disqualifiziert ist
  const isLeadDisqualified = () => {
    if (propertyData.isOwner === false) return true;
    if (isLivingAreaUnsupported()) return true;
    if (propertyData.hasGasOrOil === false) return true;
    if (propertyData.heatingOlderThan2Years === false) return true;
    if (propertyData.hasMonumentProtection === true) return true;
    if (propertyData.hasSolarThermal === true && propertyData.solarThermalRemoved === false) return true;
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Kundendaten vom bestehenden Kunden verwenden
      const leadData = {
        userId: user.id,
        customerId: customerId, // WICHTIG: customerId explizit übergeben
        produkt: 'WP',
        name: customer?.name || '',
        nachname: customer?.nachname || '',
        email: customer?.email || '',
        handy: customer?.handy || '',
        strasse: customer?.strasse || '',
        hausnummer: customer?.hausnummer || '',
        postleitzahl: customer?.postleitzahl || '',
        stadt: customer?.stadt || '',
        anrede: customer?.anrede || '',
        dienstleister: customer?.dienstleister || user?.full_name || '',
        projekt: customer?.projekt || '',
        // WP-Immobiliendaten
        isOwner: propertyData.isOwner,
        buildingType: propertyData.buildingType,
        livingAreaSize: propertyData.livingAreaSize,
        peopleInHouse: propertyData.peopleInHouse,
        buildYear: propertyData.buildYear,
        hasGasOrOil: propertyData.hasGasOrOil,
        heatingOlderThan2Years: propertyData.heatingOlderThan2Years,
        currentHeatingSystem: propertyData.currentHeatingSystem,
        hasSolarThermal: propertyData.hasSolarThermal,
        solarThermalRemoved: propertyData.solarThermalRemoved,
        hasMonumentProtection: propertyData.hasMonumentProtection,
        isQualified: isLeadQualified(),
        customerStatus: customer?.status || 'Erfasst'
      };

      const leadResponse = await addLead(leadData);

      if (!leadResponse.success) {
        setError(leadResponse.error || 'Fehler beim Hinzufügen des WP-Leads');
        return;
      }

      setSuccess('WP-Lead erfolgreich hinzugefügt!');

      // Nach 2 Sekunden zurück zur Kundendetailseite
      setTimeout(() => {
        navigate(`/customer/${customerId}`);
      }, 2000);

    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Render-Funktion für WP-Daten
  const renderWpForm = () => {
    return (
      <div className="wp-form-step">
        <h2 className="form-title">Wärmepumpe hinzufügen</h2>
        {customer && (
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
            <strong>Kunde:</strong> {customer.anrede} {customer.name} {customer.nachname}
            <br />
            <strong>E-Mail:</strong> {customer.email || 'Keine E-Mail'}
            <br />
            <strong>Telefon:</strong> {customer.handy || 'Keine Telefonnummer'}
          </div>
        )}

        {/* Eigentümer */}
        <div className="form-section">
          <label className="form-label required">Eigentümer</label>
          <div className="yes-no-buttons">
            <button
              type="button"
              className={`yes-button ${propertyData.isOwner === true ? 'selected' : ''}`}
              onClick={() => handleOwnershipChange(true)}
            >
              <div className="icon-container">
                <svg viewBox="0 0 24 24" className="yes-icon">
                  <path
                    d="M4 12.6L9 17.6L20 6.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span>Ja</span>
            </button>
            <button
              type="button"
              className={`no-button ${propertyData.isOwner === false ? 'selected' : ''}`}
              onClick={() => handleOwnershipChange(false)}
            >
              <div className="icon-container">
                <svg viewBox="0 0 24 24" className="no-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span>Nein</span>
            </button>
          </div>
          {propertyData.isOwner === false && (
            <div className="qualification-message disqualified">
              <svg viewBox="0 0 24 24" className="qualification-icon">
                <path
                  d="M5 5L19 19M5 19L19 5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Lead nicht Qualifiziert</span>
            </div>
          )}
        </div>

        {/* Gebäudeart */}
        {progression >= 2 && (
          <div className="form-section">
            <label className="form-label required">Gebäudeart</label>
            <div className="building-type-buttons">
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Einfamilienhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Einfamilienhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M50,20 L20,45 L20,80 L80,80 L80,45 Z" fill="none" stroke="currentColor" strokeWidth="3" />
                    <rect x="35" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="55" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="45" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <span>Einfamilienhaus</span>
              </button>
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Zweifamilienhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Zweifamilienhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M50,20 L15,45 L15,80 L85,80 L85,45 Z" fill="none" stroke="currentColor" strokeWidth="3" />
                    <line x1="50" y1="45" x2="50" y2="80" stroke="currentColor" strokeWidth="2" />
                    <rect x="25" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="65" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="30" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="60" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <span>Zweifamilienhaus</span>
              </button>
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Reihenhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Reihenhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M10,80 L10,45 L30,30 L30,80 Z M30,80 L30,30 L50,30 L50,80 Z M50,80 L50,30 L70,30 L70,80 Z M70,80 L70,30 L90,45 L90,80 Z" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="15" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="37" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="56" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="77" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
                <span>Reihenhaus</span>
              </button>
            </div>
          </div>
        )}

        {/* Wohnfläche */}
        {progression >= 3 && (
          <div className="form-section">
            <label className="form-label required">Wie groß ist die Wohnfläche Ihres Hauses?</label>
            <input
              type="number"
              name="livingAreaSize"
              value={propertyData.livingAreaSize}
              onChange={handleLivingAreaSizeChange}
              className="form-input"
              placeholder="z.B. 120"
              required
            />
            {isLivingAreaUnsupported() && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert - Wohnfläche über 250 qm wird nicht unterstützt</span>
              </div>
            )}
          </div>
        )}

        {/* Personen im Haus */}
        {progression >= 4 && (
          <div className="form-section">
            <label className="form-label required">Wie viele Personen Leben in Ihrem Haus?</label>
            <input
              type="number"
              name="peopleInHouse"
              value={propertyData.peopleInHouse}
              onChange={handlePeopleInHouseChange}
              className="form-input"
              placeholder="z.B. 4"
              required
            />
          </div>
        )}

        {/* Baujahr */}
        {progression >= 5 && (
          <div className="form-section">
            <label className="form-label required">Wann wurde Ihr Haus gebaut?</label>
            <input
              type="number"
              name="buildYear"
              value={propertyData.buildYear}
              onChange={handleBuildYearChange}
              className="form-input"
              placeholder="z.B. 1985"
              required
            />
          </div>
        )}

        {/* Gas oder Öl Heizung */}
        {progression >= 6 && (
          <div className="form-section">
            <label className="form-label required">Haben Sie eine Gas, Öl oder Wärmepumpen Heizung?</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button ${propertyData.hasGasOrOil === true ? 'selected' : ''}`}
                onClick={() => handleGasOrOilChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`no-button ${propertyData.hasGasOrOil === false ? 'selected' : ''}`}
                onClick={() => handleGasOrOilChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            {propertyData.hasGasOrOil === false && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Heizungsanlage älter als 10 Jahre */}
        {progression >= 7 && (
          <div className="form-section">
            <label className="form-label required">Heizungsanlage älter als 10 Jahre?</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button ${propertyData.heatingOlderThan2Years === true ? 'selected' : ''}`}
                onClick={() => handleHeatingOlderThan2YearsChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`no-button ${propertyData.heatingOlderThan2Years === false ? 'selected' : ''}`}
                onClick={() => handleHeatingOlderThan2YearsChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            {propertyData.heatingOlderThan2Years === false && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Aktuelles Wärmeabgabesystem */}
        {progression >= 8 && (
          <div className="form-section">
            <label className="form-label required">Aktuelles Wärmeabgabesystem</label>
            <div className="radio-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="currentHeatingSystem"
                  value="Fußbodenheizung"
                  checked={propertyData.currentHeatingSystem === "Fußbodenheizung"}
                  onChange={() => handleCurrentHeatingSystemChange("Fußbodenheizung")}
                />
                <span>Fußbodenheizung</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="currentHeatingSystem"
                  value="Heizkörper"
                  checked={propertyData.currentHeatingSystem === "Heizkörper"}
                  onChange={() => handleCurrentHeatingSystemChange("Heizkörper")}
                />
                <span>Heizkörper</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="currentHeatingSystem"
                  value="Fußbodenheizung und Heizkörper"
                  checked={propertyData.currentHeatingSystem === "Fußbodenheizung und Heizkörper"}
                  onChange={() => handleCurrentHeatingSystemChange("Fußbodenheizung und Heizkörper")}
                />
                <span>Fußbodenheizung und Heizkörper</span>
              </label>
            </div>
          </div>
        )}

        {/* Solarthermieanlage */}
        {progression >= 9 && (
          <div className="form-section">
            <label className="form-label required">Haben Sie eine Solarthermieanlage?</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button ${propertyData.hasSolarThermal === true ? 'selected' : ''}`}
                onClick={() => handleSolarThermalChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`yes-button ${propertyData.hasSolarThermal === false ? 'selected' : ''}`}
                onClick={() => handleSolarThermalChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
          </div>
        )}

        {/* Solarthermie wird abgeklemmt oder abgebaut */}
        {progression >= 10 && propertyData.hasSolarThermal === true && (
          <div className="form-section">
            <label className="form-label required">Solarthermie wird abgeklemmt oder abgebaut?</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button ${propertyData.solarThermalRemoved === true ? 'selected' : ''}`}
                onClick={() => handleSolarThermalRemovedChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`no-button ${propertyData.solarThermalRemoved === false ? 'selected' : ''}`}
                onClick={() => handleSolarThermalRemovedChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            {propertyData.solarThermalRemoved === false && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Denkmalschutz */}
        {progression >= 11 && (
          <div className="form-section">
            <label className="form-label required">Denkmalschutz</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`reverse-yes-button ${propertyData.hasMonumentProtection === true ? 'selected' : ''}`}
                onClick={() => handleMonumentProtectionChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`reverse-no-button ${propertyData.hasMonumentProtection === false ? 'selected' : ''}`}
                onClick={() => handleMonumentProtectionChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            {propertyData.hasMonumentProtection === true && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Qualifizierungsmeldung */}
        {progression >= 12 && !isLeadDisqualified() && isStep1Valid() && (
          <div className="qualification-message qualified">
            <svg viewBox="0 0 24 24" className="qualification-icon">
              <path
                d="M4 12.6L9 17.6L20 6.6"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Der Lead ist qualifiziert!</span>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={() => navigate(`/customer/${customerId}`)}>
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !isStep1Valid() || isLeadDisqualified()}
          >
            {loading ? 'Wird gespeichert...' : 'WP-Lead speichern'}
          </button>
        </div>
      </div>
    );
  };

  if (customerLoading) {
    return (
      <>
        <DashboardNav user={user} />
        <div style={{ padding: '20px', textAlign: 'center' }}>Lade Kundendaten...</div>
      </>
    );
  }

  if (!customerId) {
    return (
      <>
        <DashboardNav user={user} />
        <div style={{ padding: '20px', color: 'red' }}>Fehler: Keine Kunden-ID angegeben</div>
      </>
    );
  }

  if (!customer) {
    return (
      <>
        <DashboardNav user={user} />
        <div style={{ padding: '20px', color: 'red' }}>Fehler: Kunde nicht gefunden</div>
      </>
    );
  }

  return (
    <>
      <DashboardNav user={user} />
      <div className="lead-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          {renderWpForm()}
        </form>
      </div>
    </>
  );
}

export default AddWpLeadToCustomer;

